/**
 * HelpPopover – kleiner „?"-Button mit Kurzanleitung als Popover.
 *
 * Erklärt dem Benutzer, wie er Suchfelder (topic="filter") bzw. Spalten
 * (topic="columns") in den Auswertungslisten selbst anpassen kann.
 *
 * Wird geteilt in der FilterBar (Suchfelder) und neben dem Spalten-Zahnrad
 * jeder Listenseite (Spalten) verwendet – erscheint dadurch automatisch auf
 * allen jetzigen und künftigen Auswertungsmasken.
 *
 * Beim ersten Öffnen wird der Erst-Hinweis-Balken (siehe listHelp.ts)
 * dauerhaft als „gesehen" markiert.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HelpCircle, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { markListHelpSeen } from '@/lib/listHelp'
import { hilfePfad, hilfeSichtbar, useHilfe } from '@/lib/hilfe'

export interface HelpPopoverProps {
  /** Welche Anleitung angezeigt wird */
  topic: 'filter' | 'columns'
  /** Positionierung des „?"-Buttons (z.B. 'absolute right-8 top-1.5') */
  className?: string
  /**
   * AP-18 Nachtrag 1 (6.4) — Abschnitt im Handbuch, `bereich/thema#anker` (aus `HILFE_VERWEISE`).
   * Darf der Benutzer das Thema sehen, steht unter den Schritten „Mehr dazu in der Hilfe".
   */
  hilfe?: string
}

export function HelpPopover({ topic, className, hilfe }: HelpPopoverProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Schließen bei Klick außerhalb
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const handleToggle = () => {
    setOpen((v) => !v)
    // Erst-Hinweis dauerhaft ausblenden, sobald der Benutzer die Hilfe öffnet
    markListHelpSeen()
  }

  const title = t(`listHelp.${topic}.title`)
  const steps = t(`listHelp.${topic}.steps`, { returnObjects: true }) as unknown
  const stepList = Array.isArray(steps) ? (steps as string[]) : []
  const footer = t(`listHelp.${topic}.footer`)
  const hilfeInhalt = useHilfe((s) => s.inhalt)
  const verweis = hilfe ? hilfePfad(hilfe) : null
  const zeigeVerweis = !!verweis && hilfeSichtbar(hilfeInhalt, verweis.area, verweis.slug)

  return (
    <div className={`z-20 ${className ?? ''}`} ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        title={t('listHelp.open', { defaultValue: 'Hilfe' })}
        aria-label={t('listHelp.open', { defaultValue: 'Hilfe' })}
        aria-expanded={open}
      >
        <HelpCircle className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-8 w-72 rounded-lg border border-border p-4 text-left shadow-lg"
          // Inline-Style gegen die bekannte Popover-Transparenz-Eigenheit (FORM-STYLES.md → Overlays)
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
          role="dialog"
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
              aria-label={t('common.close', { defaultValue: 'Schließen' })}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
            {stepList.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {footer && (
            <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">{footer}</p>
          )}

          {zeigeVerweis && (
            <Link to={verweis.href} onClick={() => setOpen(false)} className="mt-2 inline-block text-xs font-medium text-primary hover:opacity-80">
              {t('help.more')} →
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
